import { useMemo, useState } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import {
  useCollectionBinding,
  useTokens,
} from '@vibesites/runtime/react';
import { contrastRatio, pickHighestContrast, relativeLuminance } from '../utils/luminance';

interface Faq {
  question?: string | null;
  answer?: string | null;
  category?: string | null;
}

const ALL_CATEGORY = 'All';

// Placeholder FAQs shown only when the property has no entries in the
// `faqs` collection. Deliberately generic/lorem-style so they read as
// scaffolding rather than finalized copy — editors replace them by
// adding real entries in the CMS.
const DEFAULT_FAQS: Faq[] = [
  {
    question: 'Frequently asked question 1',
    answer:
      '<p>Placeholder answer — replace this text with your own. Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.</p>',
  },
  {
    question: 'Frequently asked question 2',
    answer:
      '<p>Placeholder answer — replace this text with your own. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.</p>',
  },
  {
    question: 'Frequently asked question 3',
    answer:
      '<p>Placeholder answer — replace this text with your own. Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur.</p>',
  },
  {
    question: 'Frequently asked question 4',
    answer:
      '<p>Placeholder answer — replace this text with your own. Excepteur sint occaecat cupidatat non proident, sunt in culpa qui officia deserunt mollit anim id est laborum.</p>',
  },
  {
    question: 'Frequently asked question 5',
    answer:
      '<p>Placeholder answer — replace this text with your own. Sed ut perspiciatis unde omnis iste natus error sit voluptatem accusantium doloremque laudantium, totam rem aperiam.</p>',
  },
];

function ChevronIcon({ open }: { open: boolean }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={`faql-chevron ${open ? 'faql-chevron--open' : ''}`}
      aria-hidden="true"
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

export function FAQList() {
  const faqs = useCollectionBinding<Faq>('faqlist.items', {
    collection: 'faqs',
  });

  // Always render the accordion — if the property hasn't populated the
  // `faqs` collection, fall back to the built-in defaults so the section
  // shows its finished look immediately.
  const source = useMemo<Faq[]>(
    () => (faqs && faqs.length > 0 ? faqs : DEFAULT_FAQS),
    [faqs],
  );

  const categories = useMemo(() => {
    const seen = new Set<string>();
    for (const faq of source) {
      const cat = (faq.category ?? '').trim();
      if (cat) seen.add(cat);
    }
    return Array.from(seen);
  }, [source]);

  const [activeCategory, setActiveCategory] = useState<string>(ALL_CATEGORY);
  // First accordion item is expanded by default; other panels stay collapsed.
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const filteredFaqs = useMemo(() => {
    if (activeCategory === ALL_CATEGORY) return source;
    return source.filter((f) => (f.category ?? '').trim() === activeCategory);
  }, [source, activeCategory]);

  // Pick a readable text color for the primary-coloured accordion header.
  // Prefer the theme's `textOnPrimary` token when it clears WCAG AA against
  // primary; otherwise fall back to synthesizing black or white from the
  // primary's own luminance so the tabs stay legible on any brand color.
  const tokens = useTokens();
  const accordionTextColor = useMemo(() => {
    const primary = tokens?.color?.primary;
    if (!primary) return '#ffffff';
    const onPrimary = tokens?.color?.textOnPrimary;
    const candidates = ['#ffffff', '#111111'];
    if (typeof onPrimary === 'string' && onPrimary.length > 0) {
      candidates.unshift(onPrimary);
    }
    const picked = pickHighestContrast(primary, candidates);
    if (picked && contrastRatio(primary, picked) >= 4.5) return picked;
    return relativeLuminance(primary) > 0.55 ? '#111111' : '#ffffff';
  }, [tokens]);

  const sectionStyle = {
    ['--faql-tab-text' as string]: accordionTextColor,
  } as React.CSSProperties;

  return (
    <section
      className="bg-[var(--vibe-color-background)] py-12 md:py-20"
      style={sectionStyle}
    >
      <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
        {categories.length > 1 && (
          <div className="faql-filters" role="tablist" aria-label="FAQ categories">
            <button
              type="button"
              role="tab"
              aria-selected={activeCategory === ALL_CATEGORY}
              className={`faql-filter ${activeCategory === ALL_CATEGORY ? 'faql-filter--active' : ''}`}
              onClick={() => {
                setActiveCategory(ALL_CATEGORY);
                setOpenIndex(null);
              }}
            >
              {ALL_CATEGORY}
            </button>
            {categories.map((cat) => (
              <button
                key={cat}
                type="button"
                role="tab"
                aria-selected={activeCategory === cat}
                className={`faql-filter ${activeCategory === cat ? 'faql-filter--active' : ''}`}
                onClick={() => {
                  setActiveCategory(cat);
                  setOpenIndex(null);
                }}
              >
                {cat}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="faql-list-wrap">
        <ul className="faql-list">
          {filteredFaqs.map((faq, index) => {
            const isOpen = openIndex === index;
            const question = (faq.question ?? '').trim();
            const answer = faq.answer ?? '';
            if (!question && !answer) return null;

            const panelId = `faq-panel-${index}`;
            const buttonId = `faq-button-${index}`;

            return (
              <li key={index} className={`faql-item ${isOpen ? 'faql-item--open' : ''}`}>
                <button
                  id={buttonId}
                  type="button"
                  className="faql-question"
                  aria-expanded={isOpen}
                  aria-controls={panelId}
                  onClick={() => setOpenIndex(isOpen ? null : index)}
                >
                  <span className="faql-question-text">
                    {question || 'Question'}
                  </span>
                  <span className="faql-chevron-badge" aria-hidden="true">
                    <ChevronIcon open={isOpen} />
                  </span>
                </button>
                <div
                  id={panelId}
                  role="region"
                  aria-labelledby={buttonId}
                  className="faql-panel"
                  data-open={isOpen}
                >
                  <div className="faql-panel-inner">
                    <div
                      className="faql-answer"
                      dangerouslySetInnerHTML={{ __html: answer }}
                    />
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      </div>

      <style>{`
        .faql-filters {
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          gap: 0.5rem;
          margin-bottom: 2rem;
        }
        .faql-filter {
          appearance: none;
          background: transparent;
          border: 1px solid var(--vibe-color-border);
          color: var(--vibe-color-text);
          font-family: var(--vibe-font-body);
          font-size: 0.85rem;
          letter-spacing: 0.04em;
          text-transform: uppercase;
          padding: 0.5rem 1rem;
          cursor: pointer;
          transition: background-color 150ms ease, color 150ms ease, border-color 150ms ease;
        }
        .faql-filter:hover {
          border-color: var(--vibe-color-primary);
          color: var(--vibe-color-primary);
        }
        .faql-filter--active {
          background: var(--vibe-color-primary);
          border-color: var(--vibe-color-primary);
          color: var(--vibe-color-primary-contrast, #fff);
        }

        .faql-list-wrap {
          width: 100%;
          box-sizing: border-box;
          padding-left: clamp(1rem, 5vw, 4rem);
          padding-right: clamp(1rem, 5vw, 4rem);
        }
        .faql-list {
          list-style: none;
          margin: 0;
          padding: 0;
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
        }
        .faql-item {
          background: transparent;
        }
        .faql-question {
          all: unset;
          box-sizing: border-box;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 1rem;
          width: 100%;
          padding: 1.25rem 1.5rem;
          cursor: pointer;
          background: var(--vibe-color-primary);
          color: var(--faql-tab-text, #ffffff);
          font-family: var(--vibe-font-heading);
          font-size: 1.125rem;
          font-weight: 500;
          text-align: left;
          transition:
            background-color 200ms ease,
            filter 200ms ease,
            box-shadow 200ms ease;
        }
        .faql-question:hover {
          filter: brightness(1.05);
        }
        .faql-question:focus-visible {
          outline: 2px solid var(--vibe-color-primary);
          outline-offset: 2px;
        }
        .faql-question-text {
          flex: 1 1 auto;
          line-height: 1.35;
        }
        .faql-chevron-badge {
          flex: 0 0 auto;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 2rem;
          height: 2rem;
          border-radius: 9999px;
          background: rgba(255, 255, 255, 0.9);
          color: var(--vibe-color-primary);
          transition: background-color 200ms ease, transform 200ms ease;
        }
        .faql-item--open .faql-chevron-badge {
          background: #ffffff;
        }
        .faql-chevron {
          width: 1rem;
          height: 1rem;
          flex: 0 0 auto;
          color: currentColor;
          transition: transform 250ms ease;
        }
        .faql-chevron--open {
          transform: rotate(180deg);
        }

        .faql-panel {
          display: grid;
          grid-template-rows: 0fr;
          transition: grid-template-rows 300ms ease;
          background: #F2F2F2;
        }
        .faql-panel[data-open="true"] {
          grid-template-rows: 1fr;
        }
        .faql-panel-inner {
          overflow: hidden;
        }
        .faql-answer {
          padding: 1.25rem 1.5rem 1.5rem;
          font-family: var(--vibe-font-body);
          font-size: 1rem;
          line-height: 1.65;
          color: var(--vibe-color-text);
        }
        .faql-answer p {
          margin: 0 0 0.75em;
        }
        .faql-answer p:last-child {
          margin-bottom: 0;
        }
        .faql-answer ul,
        .faql-answer ol {
          margin: 0 0 0.75em;
          padding-left: 1.25rem;
        }
        .faql-answer ul {
          list-style: disc;
        }
        .faql-answer ol {
          list-style: decimal;
        }
        .faql-answer li {
          margin-bottom: 0.35em;
        }
        .faql-answer a {
          color: var(--vibe-color-primary);
          text-decoration: underline;
        }
        .faql-answer strong {
          font-weight: 600;
        }

        @media (prefers-reduced-motion: reduce) {
          .faql-panel,
          .faql-chevron,
          .faql-chevron-badge,
          .faql-question {
            transition: none;
          }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'FAQList',
  displayName: 'FAQ List',
  tokens: {},
  collectionBindings: {
    'faqlist.items': {
      label: 'FAQs',
      collection: 'faqs',
    },
  },
};

(FAQList as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default FAQList;
